import { paths } from '@/generated/supplier-experience-api';
import { downloadCsvFromPost } from '@/utils/downloadCsvFromPost';
import {
  PlayableInvoiceFetchOptions,
  PlayableInvoiceRequestBody,
  PlayableInvoiceSearchFilters,
} from './usePlayableInvoices';
import { TakerMarket } from './useTakerMarkets';

export type PlayableInvoicesCsvQuery = NonNullable<paths['/playable-invoices/csv']['post']['parameters']['query']>;

export interface DownloadPlayableInvoicesCsvArgs {
  makerOrganizationUuid: string;
  takerMarkets: TakerMarket[];
  search?: PlayableInvoiceSearchFilters;
  /** Sort only — CSV returns the full filtered set (no cursor/limit). */
  options?: Pick<NonNullable<PlayableInvoiceFetchOptions>, 'sortBy' | 'sortDirection'>;
  filename: string;
}

/**
 * Download playable (eligible) invoices CSV via SEA.
 * Body/query match the playable list API (same shape as `usePlayableInvoices`).
 */
export const downloadPlayableInvoicesCsv = async ({
  makerOrganizationUuid,
  takerMarkets,
  search,
  options,
  filename,
}: DownloadPlayableInvoicesCsvArgs): Promise<void> => {
  const body = {
    makerOrganizationUuid,
    supplierMarkets: takerMarkets.map((takerMarket) => ({
      marketUuid: takerMarket.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
    })),
    ...(search ? { search } : {}),
  } satisfies PlayableInvoiceRequestBody;

  const query: PlayableInvoicesCsvQuery = {
    ...(options?.sortBy && { sortBy: options.sortBy }),
    ...(options?.sortDirection && { sortDirection: options.sortDirection }),
  };

  await downloadCsvFromPost({
    path: 'api/supplier-experience/playable-invoices/csv',
    body,
    query: Object.keys(query).length > 0 ? query : undefined,
    filename,
  });
};
